import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getToken as fetchAppCheckToken,
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  signInAnonymously,
  type Auth,
  type User,
} from "firebase/auth";
import { afterFirstPaint } from "../../../../shared/lib/afterFirstPaint";
import { startAppCheckContained } from "../../../../shared/lib/appCheckStart";
import { appCheckHeaderToken, recaptchaUnavailable } from "../../../../shared/lib/appCheckToken";
import { config } from "../config/env";
import {
  ANONYMOUS_CREDENTIAL,
  NO_CREDENTIAL,
  type SessionCredential,
} from "../../../../shared/graphql/sessionEnd";
import { forceRefreshForRecovery, type ForcedIdTokenRecovery } from "../../../../shared/graphql/tokenRecovery";
import { clearRetirementPending, guestRotationSettled, recordGuestActivity } from "./guestSession";

// Firebase for the BOOKING surface.
//
// Deliberately smaller than apps/app's equivalent: no eager popup resolver and
// no ID-token retry ladder. A client here is anonymous by default; provider
// sign-in is a separate, currently disabled Book-only feature.
//
// The popup resolver in particular is absent ON PURPOSE. apps/app opts out of
// `getAuth()`'s implicit resolver because installing it at init reinstates a
// WebKit hang that left two routes stuck on "Checking your session…" forever.
// Book provider sign-in warms the resolver only on its gated sign-in route.
// `initializeAuth` keeps the explicit persistence chain and no eager resolver.

let appInstance: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let appCheckInstance: AppCheck | null = null;
let appCheckFailed = false;

export function getFirebaseApp(): FirebaseApp {
  if (appInstance) return appInstance;
  appInstance = getApps()[0] ?? initializeApp(config.firebase);
  return appInstance;
}

/**
 * App Check FIRST, always. Firebase Auth reads App Check while building each
 * request's headers, and its first request can be the startup reload of a
 * saved user (accounts:lookup). If that went without a token under Auth's
 * App Check enforcement it would fail with a non-network error, and Firebase
 * would clear the current user: a returning guest would lose the anonymous
 * UID their booking belongs to. Initializing App Check here, before Auth's
 * first initialization, makes that ordering structural rather than a matter
 * of which effect happened to run first.
 *
 * NOT BEFORE FIRST PAINT, therefore, for Auth either (perf P4): every boot
 * path reaches this through `whenAppCheckStarted` or an after-first-paint
 * effect (IdentityProvider), and the rest are gestures.
 */
export function getFirebaseAuth(): Auth {
  if (authInstance) return authInstance;
  initAppCheck();

  try {
    authInstance = initializeAuth(getFirebaseApp(), {
      // IndexedDB first so a guest's identity survives a reload — the anonymous
      // UID IS the booking's clientId, so losing it mid-flow loses the hold and
      // the entered details with it.
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    });
  } catch (error) {
    console.warn("Auth was already initialized elsewhere; adopting that instance.", error);
    authInstance = getAuth(getFirebaseApp());
  }

  return authInstance;
}

/**
 * App Check is HARD-REQUIRED by the backend on every call — the public /app
 * endpoint answers 403 (APP_CHECK_REJECTED) without a valid token, and there
 * is no unenforced grace period. Anonymous auth does not replace it.
 *
 * The provider must be classic reCAPTCHA v3, NOT ReCaptchaEnterpriseProvider.
 * A mismatched provider class makes every token fail verification.
 */
export function initAppCheck(): AppCheck | null {
  if (appCheckInstance || appCheckFailed) return appCheckInstance;

  try {
    if (config.isDevEnvironment && config.appCheckDebugToken) {
      (globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        config.appCheckDebugToken;
    }

    appCheckInstance = startAppCheckContained(
      () =>
        initializeAppCheck(getFirebaseApp(), {
          provider: new ReCaptchaV3Provider(config.appCheckSiteKey),
          isTokenAutoRefreshEnabled: true,
        }),
      // reCAPTCHA loaded without `grecaptcha`: classified, never an uncaught
      // "no recaptcha" (shared/lib/appCheckStart.ts has the SDK path).
      recaptchaUnavailable
    );
  } catch (error) {
    // Don't take the surface down. A request will surface the 401 with a
    // clearer message than a blank page would.
    appCheckFailed = true;
    console.error("App Check initialization failed; API calls will be rejected.", error);
  }

  return appCheckInstance;
}

/**
 * NOT BEFORE FIRST PAINT (perf P4, as apps/app since c2's follow-up).
 * `initializeAppCheck` injects reCAPTCHA's www.google.com/recaptcha/api.js at
 * once. Injected while the document is still loading, a content blocker that
 * DROPS it keeps `load` from firing, and WebKit holds the first paint of a
 * page below its "visually non-empty" threshold until load: Book's loading
 * skeleton may be such a page. So App Check starts after two animation frames
 * (on screen) or after load, whichever is first (shared/lib/afterFirstPaint.ts),
 * and anything that needs it before then waits the same way. Never rejects; a
 * failed start leaves App Check off exactly as initAppCheck() does.
 */
function afterFirstPaintOrLoad(): Promise<void> {
  return new Promise((resolve) => {
    afterFirstPaint(resolve);
  });
}

export async function whenAppCheckStarted(): Promise<AppCheck | null> {
  if (!appCheckInstance && !appCheckFailed) await afterFirstPaintOrLoad();
  return initAppCheck();
}

/**
 * The token for one request, through the shared attempt
 * (shared/lib/appCheckToken.ts): bounded at 10 s because a blocked reCAPTCHA
 * never settles, every attempt recorded for Try again
 * (shared/lib/appCheckRecovery.ts), and App Check's own failures reported
 * once as a fixed kind. App goes through the same function.
 */
export async function getAppCheckHeaderToken(): Promise<string | null> {
  return appCheckHeaderToken(await whenAppCheckStarted(), fetchAppCheckToken);
}

/**
 * The guest identity.
 *
 * Product's contract (Q16): web uses `signInAnonymously` BEFORE an anonymous
 * client creates a hold or booking, and the anonymous Firebase UID **is** the
 * booking's persisted `clientId`. Guest-to-account conversion later uses
 * `linkWithCredential` so the UID survives — never a new identity, and never
 * inference from a matching email or phone.
 *
 * Idempotent: an existing session is returned rather than replaced. Replacing
 * it would orphan any hold the previous UID owns, and the server tracks hold
 * ownership by the verified uid on the token.
 *
 * Not forever, though (security audit M1): `auth/guestRotation.ts` retires an
 * anonymous identity after a definite outcome or when it has been idle. A call
 * that starts while a rotation is under way waits for it, so it gets the NEW
 * identity rather than the one being signed out. Every call is recorded as
 * activity, which is what keeps a guest mid-booking from looking idle.
 */
export function ensureGuestIdentity(): Promise<User> {
  // SINGLE-FLIGHT. The identity is warmed on intent (warmGuestIdentity) and
  // then asked for again by the hold; two concurrent signInAnonymously calls
  // would mint two identities. Concurrent callers share one attempt; a failed
  // one is forgotten so the next caller tries again.
  if (!guestIdentityInFlight) {
    guestIdentityInFlight = resolveGuestIdentity().finally(() => {
      guestIdentityInFlight = null;
    });
  }
  return guestIdentityInFlight;
}

let guestIdentityInFlight: Promise<User> | null = null;

async function resolveGuestIdentity(): Promise<User> {
  await guestRotationSettled();
  const auth = getFirebaseAuth();
  // Before reading `currentUser`: until persistence is restored it reads null,
  // and `signInAnonymously` would then hand back the RESTORED user, which this
  // function would mistake for a new one.
  await auth.authStateReady();
  const existing = auth.currentUser;
  if (existing) {
    if (existing.isAnonymous) recordGuestActivity();
    return existing;
  }

  const credential = await signInAnonymously(auth);
  // A new identity: whatever retirement was pending was about one that is gone.
  clearRetirementPending();
  recordGuestActivity();
  return credential.user;
}

/**
 * WARM ON INTENT (perf D2, Web Director 2026-10-07). The slot tap used to pay
 * three round trips in series: anonymous sign-up, the SDK's account lookup,
 * then the hold. Starting the identity when the person shows intent to pick
 * a time (a day tap, or reaching for a slot) means the tap usually costs only
 * the hold.
 *
 * NEVER BEFORE AN INTENT GESTURE. Not on page load and not on the service
 * step: that would mint a pseudonymous identity for every browser that opens
 * a booking link, which edges toward tracking. Everything else is unchanged:
 * the same ensureGuestIdentity, the same rotation and idle rules, the same
 * identity claim. A failure is ignored here. A hold asked for AFTER it failed
 * starts a fresh attempt; one asked for WHILE it is still failing joins it
 * (single-flight) and shares the failure, and the next tap starts fresh.
 */
export function warmGuestIdentity(): void {
  void ensureGuestIdentity().catch(() => {});
}

/**
 * Firebase ID tokens expire hourly and the SDK refreshes within ~5 minutes of
 * expiry, so this is called per request rather than cached.
 *
 * Returns null when there is no session at all, which is the normal state for
 * someone reading a professional's profile — the public queries need only App
 * Check.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return null;

  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error("Failed to obtain a Firebase ID token.", error);
    return null;
  }
}

/**
 * NO NEW CREDENTIALED REQUEST WHILE A SIGN-OUT IS PENDING (sign-out
 * increment). When Book is signing this uid out and the SDK has not dropped
 * it yet (a hidden tab, where Firebase refuses the persistence write until
 * the tab is visible again; storage pressure), requests go out with no token,
 * so a revoked session is not sprayed with refusals from a background tab.
 * It matches that uid only, so any other identity is unaffected, and is
 * cleared when the sign-out completes (auth/signOutSafely.ts).
 */
let signingOutUid: string | null = null;

export function suppressCredentialedRequests(uid: string): void {
  signingOutUid = uid;
}

export function resumeCredentialedRequests(): void {
  signingOutUid = null;
}

/**
 * The ID token for one request AND whether it is a guest's, read from the
 * same user object after the token resolves (a link can turn the guest
 * credentialed in between; this reads what the user is when the token is
 * attached).
 *
 * THE ANONYMOUS RULE depends on this. The session link (shared/graphql/
 * sessionLink.ts) refreshes or ends a session only for a `credentialed`
 * request; an anonymous guest's UID is their booking's clientId and is never
 * signed out from the transport.
 */
export async function getRequestIdentity(): Promise<{ token: string | null; credential: SessionCredential }> {
  // The first request can arrive in the first commit; it waits for the same
  // after-first-paint start rather than initializing Auth (and so App Check)
  // before the page is on screen.
  await whenAppCheckStarted();
  const user = getFirebaseAuth().currentUser;
  if (!user) return { token: null, credential: NO_CREDENTIAL };
  if (signingOutUid !== null && user.uid === signingOutUid) return { token: null, credential: NO_CREDENTIAL };
  let token: string | null;
  try {
    token = await user.getIdToken();
  } catch (error) {
    console.error("Failed to obtain a Firebase ID token.", error);
    token = null;
  }
  if (!token) return { token: null, credential: NO_CREDENTIAL };
  return {
    token,
    credential: user.isAnonymous ? ANONYMOUS_CREDENTIAL : { kind: "credentialed", uid: user.uid },
  };
}

/**
 * A 401 with no reason on a CREDENTIALED request: refresh once through the
 * SDK, and end the session only when the SDK says the identity is invalid
 * (the rule App has always applied; shared/graphql/tokenRecovery.ts). Never
 * called for a guest.
 */
export async function forceRefreshIdTokenForRecovery(uid: string): Promise<ForcedIdTokenRecovery> {
  return forceRefreshForRecovery(getFirebaseAuth().currentUser, uid);
}
